// File: src/App.js
// Experten-gesteuertes Layout-Routing für Experts4Germany

import "./App.css";

import React, { useEffect, useState } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import RoleGate from "./components/auth/RoleGate";
import { AppearanceProvider } from "./context/AppearanceContext";

import NewsTicker from "./components/NewsTicker";              // global montiert
import config from "./config/config";                          // für API_BASE_URL

// Seiten & Dashboards
import Home from "./pages/Home";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AdminDashboard from "./pages/AdminDashboard";
import UserAdmin from "./pages/UserAdmin";
import AccountSettings from "./pages/AccountSettings";
import ApplicantDashboard from "./pages/ApplicantDashboard";
import ApplicantAchievements from "./pages/ApplicantAchievements";
import ApplicantEditProfile from "./pages/ApplicantEditProfile";
import ApplicantApplicationWorkspace from "./pages/ApplicantApplicationWorkspace";
import ApplicantB1Workspace from "./pages/ApplicantB1Workspace";
import ApplicantFilingRequests from "./pages/ApplicantFilingRequests";
import ApplicantMatches from "./pages/ApplicantMatches";
import ApplicantMessages from "./pages/ApplicantMessages";
import EmployerDashboard from "./pages/EmployerDashboard";
import PressDashboard from "./pages/PressDashboard";
import BlogAdmin from "./pages/BlogAdmin/BlogAdmin";
import BlogEditor from "./pages/BlogAdmin/BlogEditor";
import WissensTest from "./pages/WissensTest";
import WtStart from "./pages/wt/WtStart";
import WtRun from "./pages/wt/WtRun";
import WtResult from "./pages/wt/WtResult";
import WtHistory from "./pages/wt/WtHistory";
import NewsTickerAdmin from "./pages/NewsTickerAdmin";
import SecurityDashboard from "./pages/SecurityDashboard";
import SystemStatusAdmin from "./pages/SystemStatusAdmin";
import ChatBotAdmin from "./pages/ChatBotAdmin";
import ChatbotFaqAdmin from "./pages/ChatbotFaqAdmin";
import GPTMaintainerAdmin from "./pages/Admin/GPTMaintainerAdmin";
import GPTDocAgentAdmin from "./pages/Admin/GPTDocAgentAdmin";
import GPTCockpit from "./pages/Admin/GPT_Cockpit";
import WissenstestAdmin from "./pages/Admin/WissenstestAdmin";
import WissenstestManagement from "./pages/Admin/WissenstestManagement";
import AssessmentOpsAdmin from "./pages/Admin/AssessmentOpsAdmin";
import TerminalConsole from "./components/GPT_TerminalConsole";

// ➕ NEU: German-Training
import German from "./pages/German";

// STB-Komponenten
import StbPreview from "./pages/stb/StbPreview";
import StbJobMarketplace from "./pages/stb/StbJobMarketplace";
import StbEditStellenanzeige from "./pages/stb/StbEditStellenanzeige";
import StbVerwaltung from "./pages/stb/StbVerwaltung";
import StbAnzeigeVerwaltung from "./pages/stb/StbAnzeigeVerwaltung";
import StbAdvCreate from "./pages/stb/Stb_adv_create";
import StbAdvManage from "./pages/stb/Stb_adv_manage";
import StbAdvAnalyse from "./pages/stb/Stb_adv_analyse";
import StbAdvTranslate from "./pages/stb/Stb_adv_translate";
import StbApplications from "./pages/stb/StbApplications";

// Rechtliches
import Impressum from "./pages/legal/Impressum";
import AGB from "./pages/legal/AGB";
import Datenschutz from "./pages/legal/Datenschutz";

// Hauptmenü
import MnuTools from "./pages/menue/Mnu_tools";
import MnuQuali from "./pages/menue/Mnu_qualy.js";
import MnuSuccess from "./pages/menue/Mnu_success.js";
import MnuSupport from "./pages/menue/Mnu_support.js";

import WithContainer from "./layouts/WithContainer";
import NoContainer from "./layouts/NoContainer";
import RouteSeo from "./components/RouteSeo";

/** Layout-Regeln (unverändert) */
const layoutConfig = {
  "/": "no",
  "/login": "no",
  "/register": "no",
  "/dashboard/admin": "no",
  "/dashboard/account": "no",
  "/dashboard/applicant": "no",
  "/dashboard/applicant/erfolge": "no",
  "/dashboard/applicant/edit-profile": "no",
  "/dashboard/applicant/cv2": "no",
  "/dashboard/applicant/bewerbung": "no",
  "/dashboard/applicant/sprachtest-b1": "no",
  "/dashboard/applicant/filing-requests": "no",
  "/dashboard/applicant/matches": "no",
  "/dashboard/applicant/nachrichten": "no",
  "/dashboard/security": "no"
};

const fullWidthPrefixes = [
  "/dashboard/admin",
  "/dashboard/users",
  "/dashboard/employer",
  "/dashboard/stb",
  "/dashboard/security",
  "/dashboard/systemstatus",
  "/dashboard/newsticker",
  "/dashboard/chatbot-admin",
  "/dashboard/faq-admin",
  "/dashboard/gpt",
  "/dashboard/gpt-maintainer",
  "/dashboard/gpt-docagent",
  "/dashboard/blog",
  "/dashboard/blog-editor",
  "/admin/gpt-terminal",
];

function getLayout(pathname) {
  if (fullWidthPrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
    return NoContainer;
  }
  const layoutType = layoutConfig[pathname] || "yes";
  return layoutType === "no" ? NoContainer : WithContainer;
}

function ApplicantLegacyCvRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const tab = params.get("tab");
  const target = tab === "skills" ? "/dashboard/applicant/bewerbung?tab=skills" : "/dashboard/applicant/bewerbung?tab=cv";
  return <Navigate to={target} replace />;
}

/** Default-Settings für den Ticker (identisch zur Admin-UI) */
const defaultTickerSettings = {
  enabled: true,
  position: "fixed",
  placement: "top",
  width: "100%",
  background_color: "#f8fbff",
  text_color: "#0e1726",
  border: "",
  padding: "0px",
  margin: "0",
  animation_type: "marquee", // Admin kann umstellen: marquee | slide | fade | typing | none
  animation_speed: 40,       // Basiseinheit in Sekunden: je größer, desto langsamer
  pause_on_hover: true,
  autoplay: true,
  show_close_button: true,
  height_px: 58
};

function App() {
  const location = useLocation();
  const Layout = getLayout(location.pathname);
  const showNewsTicker =
    !location.pathname.startsWith("/dashboard") &&
    location.pathname !== "/" &&
    location.pathname !== "/login" &&
    location.pathname !== "/register";

  // -----------------------------
  // Globaler Newsticker-State
  // -----------------------------
  const [tickerSettings, setTickerSettings] = useState(defaultTickerSettings);
  const [tickerMessages, setTickerMessages] = useState([]);
  // Per-Message-Speed: 1 = normal, <1 = schneller, >1 = langsamer
  const [perMessageSpeed, setPerMessageSpeed] = useState([]);

  // Daten laden: erst /public (falls vorhanden), sonst /settings + /messages
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${config.API_BASE_URL}/api/newsticker/public`);
        if (r.ok) {
          const data = await r.json();
          const settings = { ...defaultTickerSettings, ...(data?.settings || {}) };
          const messages = Array.isArray(data?.messages) ? data.messages : [];
          setTickerSettings(settings);
          setTickerMessages(messages);

          // Dein Wunsch: 3 Texte mit unterschiedlicher Geschwindigkeit
          setPerMessageSpeed(messages.length === 3 ? [1.0, 0.75, 1.5] : new Array(messages.length).fill(1));
          return;
        }
      } catch (_) {
        // ignorieren – wir fallen unten auf die Einzelrouten zurück
      }
      try {
        const [s, m] = await Promise.all([
          fetch(`${config.API_BASE_URL}/api/newsticker/settings`),
          fetch(`${config.API_BASE_URL}/api/newsticker/messages`)
        ]);
        const settings = s.ok ? await s.json() : {};
        const messages = m.ok ? await m.json() : [];
        setTickerSettings({ ...defaultTickerSettings, ...settings });
        setTickerMessages(Array.isArray(messages) ? messages : []);
        setPerMessageSpeed(Array.isArray(messages) && messages.length === 3 ? [1.0, 0.75, 1.5] : new Array(messages.length).fill(1));
      } catch (e) {
        console.warn("NewsTicker: Konnte Daten nicht laden:", e);
        // bei Fehler einfach nichts anzeigen (messages bleibt leer)
      }
    })();
  }, []);

  // Bestehende Body-Hintergründe je nach Route
  useEffect(() => {
    const path = location.pathname;
    console.log("start APP.js", path);

    if (path === "/" || path.startsWith("/stb")) {
      document.body.classList.add("landingpage-mode");
      document.body.style.background = "#000";
      document.body.style.backgroundImage = "none";
    } else {
      document.body.classList.remove("landingpage-mode");
      document.body.style.background = "";
      document.body.style.backgroundImage = "";
      document.body.style.backgroundSize = "";
      document.body.style.backgroundPosition = "";
    }
  }, [location]);

  return (
    <AppearanceProvider>
      <>
        <RouteSeo />

        {showNewsTicker ? (
          <NewsTicker
            messages={tickerMessages}
            settings={tickerSettings}
            perMessageSpeed={perMessageSpeed}
          />
        ) : null}

        <Layout>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/dashboard/admin" element={<RoleGate allow={["admin"]}><AdminDashboard /></RoleGate>} />
            <Route path="/dashboard/users" element={<RoleGate allow={["admin"]}><UserAdmin /></RoleGate>} />
            <Route path="/dashboard/admin/users" element={<RoleGate allow={["admin"]}><UserAdmin /></RoleGate>} />
            <Route path="/dashboard/applicant" element={<RoleGate allow={["applicant"]}><ApplicantDashboard /></RoleGate>} />
            <Route path="/dashboard/applicant/erfolge" element={<RoleGate allow={["applicant"]}><ApplicantAchievements /></RoleGate>} />
            <Route path="/dashboard/account" element={<RoleGate allow={["applicant", "employer", "press"]}><AccountSettings /></RoleGate>} />
            <Route path="/dashboard/applicant/edit-profile" element={<RoleGate allow={["applicant"]}><ApplicantEditProfile /></RoleGate>} />
            <Route path="/dashboard/applicant/bewerbung" element={<RoleGate allow={["applicant"]}><ApplicantApplicationWorkspace /></RoleGate>} />
            <Route path="/dashboard/applicant/sprachtest-b1" element={<RoleGate allow={["applicant"]}><ApplicantB1Workspace /></RoleGate>} />
            <Route path="/dashboard/applicant/filing-requests" element={<RoleGate allow={["applicant"]}><ApplicantFilingRequests /></RoleGate>} />
            <Route path="/dashboard/applicant/matches" element={<RoleGate allow={["applicant"]}><ApplicantMatches /></RoleGate>} />
            <Route path="/dashboard/applicant/nachrichten" element={<RoleGate allow={["applicant"]}><ApplicantMessages /></RoleGate>} />
            <Route path="/dashboard/applicant/cv2" element={<RoleGate allow={["applicant"]}><ApplicantLegacyCvRedirect /></RoleGate>} />
            <Route path="/dashboard/employer" element={<RoleGate allow={["employer"]}><EmployerDashboard /></RoleGate>} />
            <Route path="/dashboard/press" element={<RoleGate allow={["press"]}><PressDashboard /></RoleGate>} />
            <Route path="/dashboard/blog" element={<RoleGate allow={["admin"]}><BlogAdmin /></RoleGate>} />
            <Route path="/dashboard/blog-editor/:articleId" element={<RoleGate allow={["admin"]}><BlogEditor /></RoleGate>} />
            <Route path="/dashboard/wissenstest" element={<RoleGate allow={["admin", "applicant"]}><WissensTest /></RoleGate>} />
            <Route path="/assessment/wt" element={<RoleGate allow={["admin", "applicant"]}><WtStart /></RoleGate>} />
            <Route path="/assessment/wt/run/:attemptId" element={<RoleGate allow={["admin", "applicant"]}><WtRun /></RoleGate>} />
            <Route path="/assessment/wt/result/:attemptId" element={<RoleGate allow={["admin", "applicant"]}><WtResult /></RoleGate>} />
            <Route path="/assessment/wt/history" element={<RoleGate allow={["admin", "applicant"]}><WtHistory /></RoleGate>} />
            <Route path="/dashboard/admin/wissenstest" element={<RoleGate allow={["admin"]}><WissenstestAdmin /></RoleGate>} />
            <Route path="/dashboard/admin/wissenstest-management" element={<RoleGate allow={["admin"]}><WissenstestManagement /></RoleGate>} />
            <Route path="/dashboard/admin/assessment-ops" element={<RoleGate allow={["admin"]}><AssessmentOpsAdmin /></RoleGate>} />
            <Route path="/dashboard/newsticker" element={<RoleGate allow={["admin"]}><NewsTickerAdmin /></RoleGate>} />
            <Route path="/dashboard/security" element={<RoleGate allow={["admin"]}><SecurityDashboard /></RoleGate>} />
            <Route path="/dashboard/systemstatus" element={<RoleGate allow={["admin"]}><SystemStatusAdmin /></RoleGate>} />
            <Route path="/dashboard/chatbot-admin" element={<RoleGate allow={["admin"]}><ChatBotAdmin /></RoleGate>} />
            <Route path="/dashboard/faq-admin" element={<RoleGate allow={["admin"]}><ChatbotFaqAdmin /></RoleGate>} />
            <Route path="/dashboard/gpt-maintainer" element={<RoleGate allow={["admin"]}><GPTMaintainerAdmin /></RoleGate>} />
            <Route path="/dashboard/gpt-docagent" element={<RoleGate allow={["admin"]}><GPTDocAgentAdmin /></RoleGate>} />
            <Route path="/admin/gpt-terminal" element={<RoleGate allow={["admin"]}><TerminalConsole /></RoleGate>} />
            <Route path="/dashboard/gpt" element={<RoleGate allow={["admin"]}><GPTCockpit /></RoleGate>} />

            {/* ➕ NEU: German-Training */}
            <Route path="/dashboard/german" element={<RoleGate allow={["admin", "applicant"]}><German /></RoleGate>} />

            <Route path="/stb/job-marketplace" element={<StbJobMarketplace />} />
            <Route path="/stb/preview/:id" element={<StbPreview />} />
            <Route path="/dashboard/stb/edit/:id" element={<RoleGate allow={["admin", "employer"]}><StbEditStellenanzeige /></RoleGate>} />
            <Route path="/dashboard/stb/verwaltung" element={<RoleGate allow={["admin", "employer"]}><StbVerwaltung /></RoleGate>} />
            <Route path="/dashboard/stb/anzeigen" element={<RoleGate allow={["admin", "employer"]}><StbAnzeigeVerwaltung /></RoleGate>} />
            <Route path="/dashboard/stb/create" element={<RoleGate allow={["admin", "employer"]}><StbAdvCreate /></RoleGate>} />
            <Route path="/dashboard/stb/manage" element={<RoleGate allow={["admin", "employer"]}><StbAdvManage /></RoleGate>} />
            <Route path="/dashboard/stb/applications" element={<RoleGate allow={["admin", "employer"]}><StbApplications /></RoleGate>} />
            <Route path="/dashboard/stb/preview/:id" element={<RoleGate allow={["admin", "employer"]}><StbPreview /></RoleGate>} />
            <Route path="/dashboard/stb/analyse/:id" element={<RoleGate allow={["admin", "employer"]}><StbAdvAnalyse /></RoleGate>} />
            <Route path="/dashboard/stb/translate/:adv_id" element={<RoleGate allow={["admin", "employer"]}><StbAdvTranslate /></RoleGate>} />

            <Route path="/impressum" element={<Impressum />} />
            <Route path="/agb" element={<AGB />} />
            <Route path="/datenschutz" element={<Datenschutz />} />

            <Route path="/menue/tools" element={<MnuTools />} />
            <Route path="/menue/quali" element={<MnuQuali />} />
            <Route path="/menue/success" element={<MnuSuccess />} />
            <Route path="/menue/support" element={<MnuSupport />} />
          </Routes>
        </Layout>
      </>
    </AppearanceProvider>
  );
}

export default App;
